<template>
  <div class="block lg:hidden w-24 ds:w-auto">
    <Menu as="div" class="relative min-w-[40px]">
      <MenuButton
        :aria-label="$t('common.menu')"
        class="flex h-[35px] w-[40px] items-center justify-center rounded-control"
      >
        <FontAwesomeIcon
          :icon="['fal', 'bars']"
          class="text-2xl text-white ds:text-(--ds-text-secondary)"
        />
      </MenuButton>

      <MenuItems
        as="div"
        class="absolute top-[57px] w-[200px] bg-white p-[5px_15px_10px_15px] text-fg-cool subpixel-antialiased shadow-[0_5px_6px_rgb(52,58,64,0.10)] overflow-y-auto max-h-[470px]"
      >
        <MenuItem v-slot="{ active }" as="template">
          <button
            type="button"
            class="block w-full cursor-pointer border-b border-dashed border-line-gray py-3 text-left text-xs font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            :class="active ? 'bg-(--ds-surface-brand)' : ''"
            @click="router.get(useRoute('dashboard'))"
          >
            {{ $t('common.my_events') }}
          </button>
        </MenuItem>
        <MenuItem
          v-if="
            !isMenuEntryHidden('planner_org') &&
            (usePage().props._planner?.org?.canView === true ||
              usePage().props._planner?.org?.hidden === true ||
              usePage().props._planner?.org?.canStart === true)
          "
          as="div"
          class="block cursor-pointer border-b border-dashed border-line-gray py-3 text-xs font-bold"
          @click="router.get(useRoute('planner.org.tasks', { from: 'menu' }))"
        >
          {{ $t('planner.org.title') }}
          <span
            v-if="isPlannerTrialRunning(usePage())"
            class="ml-1 rounded-full bg-(--ds-surface-brand) px-1.5 py-px text-[11px] font-semibold text-(--ds-text-brand)"
            data-test="mobile-planner-badge"
            v-text="$t('marketplace.state.trial')"
          />
        </MenuItem>
        <MenuItem
          v-if="
            usePermission('contact:list') &&
            hasCrmAccess() &&
            !isMenuEntryHidden('crm')
          "
          v-slot="{ active }"
          as="template"
        >
          <button
            type="button"
            class="block w-full cursor-pointer border-b border-dashed border-line-gray py-3 text-left text-xs font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            :class="active ? 'bg-(--ds-surface-brand)' : ''"
            data-test="mobile-crm-link"
            @click="router.get(useRoute('contacts.index'))"
          >
            {{ $t('contact.my_contacts') }}
            <span
              class="ml-1 rounded-full bg-(--ds-surface-brand) px-1.5 py-px text-[11px] font-semibold text-(--ds-text-brand)"
              data-test="mobile-crm-badge"
              v-text="$t('sidebar.badge.basis')"
            />
          </button>
        </MenuItem>
        <MenuItem
          v-if="
            (useFeature('marketplace') || useFeature('dam')) &&
            usePermission('dam:view') &&
            !isMenuEntryHidden('dam')
          "
          v-slot="{ active }"
          as="template"
        >
          <button
            type="button"
            class="block w-full cursor-pointer border-b border-dashed border-line-gray py-3 text-left text-xs font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            :class="active ? 'bg-(--ds-surface-brand)' : ''"
            data-test="mobile-asset-library-link"
            @click="router.get(orgMediaMenuHref())"
          >
            {{ $t('dam.nav.library') }}
            <span
              v-if="isDamTrialRunning(usePage())"
              class="ml-1 rounded-full bg-(--ds-surface-brand) px-1.5 py-px text-[11px] font-semibold text-(--ds-text-brand)"
              data-test="mobile-asset-library-badge"
              v-text="$t('marketplace.state.trial')"
            />
          </button>
        </MenuItem>
        <MenuItem
          v-if="
            (useFeature('marketplace') || useFeature('content')) &&
            usePermission('content:text:list')
          "
          v-slot="{ active }"
          as="template"
        >
          <button
            type="button"
            class="block w-full cursor-pointer border-b border-dashed border-line-gray py-3 text-left text-xs font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            :class="active ? 'bg-(--ds-surface-brand)' : ''"
            data-test="mobile-texts-link"
            @click="router.get(orgTextsMenuHref())"
          >
            {{ $t('content.texts.title') }}
          </button>
        </MenuItem>
        <div v-if="pageHaveEvent">
          <template v-for="menu in activeMenuElements" :key="menu.label">
            <!-- Check if menu has child and dropdown -->
            <template v-if="!menu.desktopOnly && menu.dropdown">
              <div class="block pt-3 pb-1 text-xs font-bold text-black">
                {{ $t(`common.${menu.label}`) }}
              </div>
              <div class="pl-2">
                <template v-for="child in menu.child" :key="child.label">
                  <!-- Check if child has another submenu -->
                  <template v-if="menu.childSubmenu">
                    <MenuItem
                      v-for="childSubmenu in child.submenu"
                      :key="childSubmenu.label"
                      v-slot="{ active }"
                      as="template"
                    >
                      <button
                        type="button"
                        class="block w-full cursor-pointer border-b border-dashed border-line-gray py-3 text-left text-xs font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        :class="[
                          menu.desktopOnly && 'text-fg-pale',
                          active ? 'bg-(--ds-surface-brand)' : '',
                        ]"
                        @click="
                          router.get(
                            useRoute(childSubmenu.route, {
                              event: pageHaveEvent?.id,
                            }),
                          )
                        "
                      >
                        {{ $t(`common.${childSubmenu.label}`) }}
                      </button>
                    </MenuItem>
                  </template>

                  <MenuItem v-else v-slot="{ active }" as="template">
                    <button
                      type="button"
                      class="block w-full cursor-pointer border-b border-dashed border-line-gray py-3 text-left text-xs font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                      :class="[
                        menu.desktopOnly && 'text-fg-pale',
                        active ? 'bg-(--ds-surface-brand)' : '',
                      ]"
                      @click="
                        router.get(
                          useRoute(child.route, { event: pageHaveEvent?.id }),
                        )
                      "
                    >
                      {{ $t(`common.${child.label}`) }}
                    </button>
                  </MenuItem>
                </template>
              </div>
            </template>

            <MenuItem v-else v-slot="{ active }" as="template">
              <button
                type="button"
                class="block w-full cursor-pointer border-b border-dashed border-line-gray py-3 text-left text-xs font-bold focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                :class="[
                  menu.desktopOnly && 'text-fg-pale',
                  active ? 'bg-(--ds-surface-brand)' : '',
                ]"
                @click="
                  menu.desktopOnly
                    ? (isDisabledMenuAlertShown = true)
                    : router.get(
                        useRoute(menu.route, { event: pageHaveEvent?.id }),
                      )
                "
              >
                {{ $t(`common.${menu.label}`) }}
              </button>
            </MenuItem>
          </template>
        </div>
      </MenuItems>
    </Menu>
  </div>

  <DisabledMenuAlert
    :show="isDisabledMenuAlertShown"
    @confirm="isDisabledMenuAlertShown = false"
  />
</template>

<script setup>
import { orgMediaMenuHref, orgTextsMenuHref } from '@/composables/orgContent'
import DisabledMenuAlert from '@/Components/DisabledMenuAlert.vue'
import { usePermission } from '@/composables/permission'
import { useFeature } from '@/composables/feature'
import { useRoute } from '@/composables/route'
import { hasCrmAccess } from '@/composables/subscription'
import {
  isDamTrialRunning,
  isPlannerTrialRunning,
} from '@/composables/trialNavigation'
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue'
import { router, usePage } from '@inertiajs/vue3'
import { ref, computed } from 'vue'
import {
  isMenuEntryHidden,
  useVisibleMenuElements,
} from '@/composables/navigationPreferences'

const pageHaveEvent = computed(() => usePage().props.event)
const activeMenuElements = computed(() => useVisibleMenuElements())

const isDisabledMenuAlertShown = ref(false)
</script>
